import { useEffect, useState, useId, useMemo } from "react";
import { useBreadcrumbs } from "./BreadcrumbContext.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import AppBreadcrumb from "./AppBreadcrumb.tsx";
import { Button } from "./ui/button.tsx";
import {
  AlertCircleIcon,
  CheckIcon,
  EllipsisVertical,
  FileIcon,
  FileQuestionIcon,
  PlusIcon,
  UploadIcon,
} from "lucide-react";
import { FileItem, TagValueModel, UploadStatus } from "../utils/Types.ts";
import { useUploadFile, useDeleteFile } from "../utils/Api.ts";
import { useAuth } from "@/utils/AuthContext.tsx";
import { useFiles, useTag } from "@/utils/Api.ts";
import { DataTable } from "./DataTable.tsx";
import { ColumnDef } from "@tanstack/react-table";
import { EstimateView } from "./EstimateView.tsx";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TagDropdown from "./TagDropdown.tsx";

const initialBreadcrumb = [
  { name: "Files", path: "/sources/files" },
  { name: "All Files", path: "/sources/files" },
];

export function Files() {
  const { token, currentAccount } = useAuth();
  const navigate = useNavigate();
  const [isDragging, setDragging] = useState(false);
  const { updateBreadcrumbs } = useBreadcrumbs();
  const [selectedFile, setSelectedFile] = useState<string | undefined>(undefined);
  const [currentProject, setCurrentProject] = useState<string | undefined>(
    sessionStorage.getItem("accountProject") || undefined
  );
  const [projectValues, setProjectValues] = useState<TagValueModel[]>([]);
  const { fileId } = useParams();

  const { data: files = [], isFetching, error } = useFiles(currentAccount?.id!, currentProject, token ?? undefined);

  useEffect(() => {
    setSelectedFile(fileId);
    updateBreadcrumbs(initialBreadcrumb);
  }, [fileId]);

  const { mutate: upload } = useUploadFile(currentAccount?.id!, token!);
  const {
    data: project,
    isFetching: isFetchingProjects,
    isError: isErrorProjects,
  } = useTag("project", currentAccount?.id!, token ?? undefined);

  useMemo(() => {
    //used as the "account-wide" option for project selection
    const globalProject: TagValueModel = {
      name: "*",
      path: "tags/project/scope/account/value/*",
      label: "Account-Wide",
      hint: "",
      group: "account",
      ordinal: 0,
      description: "",
    };
    if (project) {
      const values = [globalProject];
      for (const scope of project.scopes) {
        values.push(...scope.values);
      }
      setProjectValues(values);
    } else {
      setProjectValues([]);
    }
  }, [project]);

  const handleFiles = (files: FileList | null) => {
    if (!files || !files.length) return;

    //todo: do as a loop for multiple files?
    const item = {
      id: `tmp-${crypto.randomUUID()}`,
      filename: files[0].name,
      size: files[0].size,
      contentType: files[0].type,
      type: "estimate",
      status: UploadStatus.Pending,
      progress: 0,
      project: currentProject!,
    };

    upload(
      { file: files[0], item: item },
      {
        onSuccess: (newFile) => setSelectedFile(newFile.id),
        onError: () => console.log("Upload failed"),
      }
    );
  };

  const onDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    if (!currentProject) return;
    e.preventDefault();
    if (!isDragging) setDragging(true);
  };

  const onDragLeave: React.DragEventHandler<HTMLDivElement> = () => setDragging(false);

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    if (!currentProject) return;
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const onFileInput: React.ChangeEventHandler<HTMLInputElement> = (e) => handleFiles(e.target.files);

  const ringBase = "border-2 rounded-xl transition-colors duration-150 ease-out ml-[2px]";
  const ringIdle = "border-none";
  const ringActive = "border-blue-500 ring-2 ring-blue-300";
  const id = useId();

  const { mutate: removeFile } = useDeleteFile(currentAccount?.id, token ?? undefined);
  const onDeleteFile = (id: string) => {
    removeFile({ fileId: id });
  };

  const handleClose = () => {
    navigate("/sources/files");
  };

  const handleProjectChange = (value?: TagValueModel) => {
    setCurrentProject(value?.path);
    sessionStorage.setItem("accountProject", value?.path ?? "");
  };

  const columns: ColumnDef<FileItem>[] = [
    {
      accessorKey: "filename",
      header: "Filename",
      cell: ({ row }) => {
        const item = row.original;
        const fileName = row.getValue("filename") as string;
        return (
          <div className="flex items-center gap-2">
            <FileIcon strokeWidth={2.5} className="w-4 h-4" />
            <NavLink to={`/sources/files/${item.id}`}>{fileName}</NavLink>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as UploadStatus;
        switch (status) {
          case UploadStatus.Success:
            return <CheckIcon size="1em" strokeWidth={2} />;
          case UploadStatus.Pending:
            return <UploadIcon size="1em" strokeWidth={2} />;
          case UploadStatus.Error:
            return <AlertCircleIcon size="1em" strokeWidth={2} />;
          default:
            return <FileQuestionIcon size="1em" strokeWidth={2} />;
        }
      },
    },
    {
      accessorKey: "size",
      header: "Mb",
      cell: ({ row }) => {
        const bytes = Number(row.getValue("size"));
        const mb = bytes / 1_000_000;
        const s = mb.toFixed(2).replace(/\.00$/, ""); // "12.00" -> "12", "0.10" stays "0.10"
        return <div className="text-center">{s}</div>;
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const item = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open Menu</span>
                <EllipsisVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onDeleteFile(item.id)}>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="w-full overflow-y-scroll flex flex-col pb-2">
      <div className="flex flex-row items-center pt-2 h-[72px] sm:h-[96px] pl-1 text-base">
        <AppBreadcrumb />
      </div>

      <div className="flex flex-col gap-4 pt-2 max-w-[640px] gap-y-8 grow">
        <div
          className={`${ringBase} ${isDragging ? ringActive : ringIdle}` + " h-full p-2"}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <div className="flex gap-2">
            <TagDropdown
              isFetching={isFetchingProjects}
              isError={isErrorProjects}
              values={projectValues}
              onSelectionChange={handleProjectChange}
              defaultValue={currentProject}
              defaultText="Select A Project"
            />
          </div>
          {currentProject && (
            <>
              <div className="flex flex-row py-4 justify-end">
                <input
                  id={id}
                  type="file"
                  accept="application/pdf"
                  className="sr-only"
                  onChange={onFileInput}
                  disabled={isFetching || error != null}
                />
                <Button asChild type="button" variant="ghost">
                  <label htmlFor={id}>
                    <PlusIcon /> Add File
                  </label>
                </Button>
              </div>
              {error && <div className="text-red-500">Error loading files: {error.message}</div>}
              {!isFetching && !error && currentProject && <DataTable columns={columns} data={files} />}
              {isFetching && !error && (
                <div className="flex flex-col gap-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selectedFile && <EstimateView fileId={selectedFile} onClose={handleClose} />}
    </div>
  );
}
